import { useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { useIsProjectDemo } from '@/features/projectDemo';
import clsx from 'clsx';
import { Icon } from '@/components';
import { getWorklogStatusText, WorkLogStatusIconMap } from '@/features/types';
import TagManager from 'react-gtm-module';
import { Badge, Flex, Popover } from '@mantine/core';
import type { CustomListBasics } from '@/features/types';
import { GoogleEvents } from '@/features/googleEvents';
import CustomListBadgeLink from '@/views/list/custom-lists/CustomListBadgeLink';
import { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import classes from './StatusAndTagsRow.module.css';
import { useAppDispatch } from '@/app/hooks';
import { clearItem } from '@/features/analysisMapSlice';
import { updateWorklogStatus } from '@/features/worklogSlice';
import { generatePath } from 'react-router-dom';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { LISTS_DETAIL_PATH } from '@/app/constants';

/**
 * Renders work status badge and plan tags in a single horizontal row.
 * Tags that don't fit show a "+N" overflow badge with a dropdown.
 */
export function StatusAndTagsRow({
  workStatus,
  lists,
  level2,
  level3,
}: {
  workStatus?: WorkLogStatus | null;
  lists: CustomListBasics[];
  level2?: string;
  level3: string;
}) {
  const dispatch = useAppDispatch();
  const navigateWithSearch = useNavigateWithSearch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [overflowCount, setOverflowCount] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const children = Array.from(container.children) as HTMLElement[];
      const containerRight = container.getBoundingClientRect().right;
      let hidden = 0;
      for (const child of children) {
        if (child.getBoundingClientRect().right > containerRight + 1) {
          hidden++;
        }
      }
      setOverflowCount(hidden);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [lists, workStatus]);

  const isProjectDemo = useIsProjectDemo();
  const visibleLists = isProjectDemo ? [] : lists;
  const statusIcon = workStatus
    ? WorkLogStatusIconMap.get(workStatus)
    : undefined;
  if (!statusIcon && visibleLists.length === 0) return null;

  const isWorkNeeded = workStatus === WorkLogStatus.WORK_NEEDED;
  // Without a circuit there is no span route, so the badge falls back to the list.
  const spanId = level2 ? `${btoa(level2)}~${btoa(level3)}` : undefined;

  // Closes the span detail panel before navigating so stale state doesn't persist.
  const handleListClick = (
    e: MouseEvent<HTMLAnchorElement>,
    listId: string
  ) => {
    e.preventDefault();
    dispatch(clearItem());
    dispatch(updateWorklogStatus(false));
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.entry.spanDetailAffordance,
      },
    });
    navigateWithSearch(generatePath(LISTS_DETAIL_PATH, { id: listId }));
  };

  const renderListBadge = (list: CustomListBasics) => (
    <CustomListBadgeLink
      key={list.listId}
      listId={list.listId}
      name={list.name}
      spanId={spanId}
      onClick={(e) => handleListClick(e, list.listId)}
    />
  );

  return (
    <Flex gap="xs" align="center" wrap="nowrap">
      <div ref={containerRef} className={classes.tagContainer}>
        {workStatus && statusIcon && (
          <div
            className={clsx(
              classes.workStatusWrapper,
              isWorkNeeded && classes.workStatusWrapperNeeded
            )}
            data-testid="work-status-badge"
          >
            <Icon size={16} name={statusIcon} />
            <span
              className={clsx(
                classes.workStatus,
                isWorkNeeded && classes.workStatusNeeded
              )}
            >
              {getWorklogStatusText(workStatus)}
            </span>
          </div>
        )}
        {visibleLists.map((list) => (
          <span key={list.listId} className={classes.tagItem}>
            {renderListBadge(list)}
          </span>
        ))}
      </div>
      {overflowCount > 0 && (
        <Popover position="bottom" withArrow>
          <Popover.Target>
            <Badge
              className={classes.overflowBadge}
              tt="none"
              radius="sm"
              fz="xs"
              fw={500}
            >
              +{overflowCount}
            </Badge>
          </Popover.Target>
          <Popover.Dropdown>
            <Flex direction="column" gap="xs">
              {lists.slice(lists.length - overflowCount).map(renderListBadge)}
            </Flex>
          </Popover.Dropdown>
        </Popover>
      )}
    </Flex>
  );
}
